import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { TranslateService } from '@ngx-translate/core';

import { IonInput, IonPopover } from '@ionic/angular';

import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import {
  BrowserCountryPickerModalPage,
  BrowserCountryPickerModalPageIdentifier,
} from '../../../modals/browser-country-picker-modal/browser-country-picker-modal.page';
import { ContactModalPage, ContactModalPageIdentifier } from '../../../modals/contact-modal/contact-modal.page';

import { ContactType } from '../../../core/enums/contact-type.enum';

import { AccountService } from '../../../core/services/account.service';
import { AppEventsService } from '../../../core/services/app-events.service';
import { CartService } from '../../../core/services/cart.service';
import { DeepLinkService } from '../../../core/services/deep-link.service';
import { LoginService } from '../../../core/services/login.service';
import { ModalService } from '../../../core/services/modal.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { OrdersService } from '../../../core/services/orders.service';
import { SettingsService } from '../../../core/services/settings.service';
import { PlatformService } from '../../../core/services/ssr/platform.service';
import { WindowService } from '../../../core/services/ssr/window.service';

import { AppConfig, TOKEN_CONFIG } from '../../../core/config/app.config';

@Component({
  selector: 'app-browser-header',
  templateUrl: './browser-header.component.html',
  styleUrls: ['./browser-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrowserHeaderComponent implements OnInit, OnDestroy {
  @Input()
  public searchPlaceholder?: string;

  @Input()
  public emitSearchSelectedEvent?: boolean;

  @Input()
  public editableSearchBarInput?: boolean;

  @Input()
  public hideSearchBar?: boolean;

  @Input()
  public hideHeaderButtons?: boolean;

  @ViewChild('searchBar')
  public searchBarElement: IonInput;

  @Output()
  public searchSelected = new EventEmitter<void>();

  @ViewChild('contactPopover')
  public contactPopover: IonPopover;

  @ViewChild('accountPopover')
  public accountPopover: IonPopover;

  public isLoggedIn: boolean;
  public adminUrl?: string;
  public selectedLanguageCode: string;
  public selectedCountry: string;
  public selectedCountryFlagUrl: string;
  public isReferralProgramEnabled: boolean;
  public referralProgramMenuText: string;
  public walletCredits: number;
  public isAccountPopoverOpen = false;
  public isContactPopoverOpen = false;
  public contactPhoneNumber: string;
  public contactEmail: string;
  public contactWhatsappUrl: SafeUrl;

  public cartItemsCount$: Observable<number>;
  public activeOrdersCount$: Observable<number>;

  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private modalService: ModalService,
    private windowService: WindowService,
    private navigationService: NavigationService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
    private appEventsService: AppEventsService,
    private accountService: AccountService,
    private ordersService: OrdersService,
    private translateService: TranslateService,
    private loginService: LoginService,
    private cartService: CartService,
    private deepLinkService: DeepLinkService,
    private sanitizer: DomSanitizer,
    @Inject(TOKEN_CONFIG) private config: AppConfig,
  ) {}

  ngOnInit() {
    if (this.platformService.isBrowser && this.windowService.isWindowDefined) {
      this.windowService.window.addEventListener('resize', () => this.hidePopovers());
      this.initialize();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public getSearchBarElement(): IonInput {
    return this.searchBarElement ?? undefined;
  }

  public onOpenCartPage(): void {
    this.deepLinkService.openCartPage();
  }

  public onRedirectToHomePage(): void {
    void this.navigationService.navigateTo('', true);
  }

  public onHandleSearch(): void {
    if (this.emitSearchSelectedEvent) {
      this.searchSelected.emit();
      return;
    }

    if (this.editableSearchBarInput) {
      return;
    }

    void this.navigationService.navigatePreservingFragmentAndQueryParamsTo('/search');
  }

  public onShowContactPopover(e: Event): void {
    this.contactPopover.event = e;
    this.isContactPopoverOpen = true;
  }

  public onSendFeedback(): void {
    this.onHideContactPopover();

    void this.modalService.showModal({
      component: ContactModalPage,
      id: ContactModalPageIdentifier,
      componentProps: { contactType: ContactType.Contact },
    });
  }

  public onShowAccountPopover(e: Event): void {
    this.accountPopover.event = e;
    this.isAccountPopoverOpen = true;
  }

  public onLogout(): void {
    this.hideAccountPopover();
    this.accountService.logOut();
  }

  public onLogin(): void {
    this.hideAccountPopover();
    void this.loginService.showSocialLogin();
  }

  public onChangeCountry(): void {
    this.hideAccountPopover();

    void this.modalService.showSheet<void>({
      id: BrowserCountryPickerModalPageIdentifier,
      component: BrowserCountryPickerModalPage,
      breakpoints: undefined,
      initialBreakpoint: undefined,
      cssClass: 'auto-height',
    });
  }

  public onChangeLanguage(): void {
    this.hideAccountPopover();

    const newLanguage = this.settingsService.getAvailableLanguages().filter((language) => language.value !== this.selectedLanguageCode)[0];

    this.navigationService.switchLanguage(newLanguage.value);
  }

  public onHideContactPopover(): void {
    void this.contactPopover?.dismiss();
  }

  public hidePopovers(): void {
    this.hideAccountPopover();
    this.onHideContactPopover();
  }

  public openAdminPortal(): void {
    this.hidePopovers();
    this.windowService.window.location.href = this.adminUrl;
  }

  private hideAccountPopover(): void {
    void this.accountPopover?.dismiss();
  }

  private initialize() {
    const { name, flagUrl, contactPhoneNumber } = this.settingsService.getCountry();
    const { value } = this.settingsService.getLanguage();

    this.selectedCountry = name;
    this.selectedCountryFlagUrl = flagUrl;
    this.selectedLanguageCode = value;
    this.contactPhoneNumber = contactPhoneNumber;
    this.contactEmail = this.config.contactEmail;

    // The settings should be always loaded at this point even if there's no
    // internet connection as the app would use the local file but still we
    // should check if the settings are ready before using them as this header
    // is also used in the ErrorPage
    // https://bilbayt.atlassian.net/browse/CA-2839
    if (this.settingsService.isInitialized()) {
      this.contactWhatsappUrl = this.sanitizer.bypassSecurityTrustUrl(this.settingsService.getWhatsAppChatLink());

      this.isReferralProgramEnabled = this.settingsService.isReferralProgramFeatureEnabled();
      this.referralProgramMenuText = this.translateService.instant('AFFILIATE_PROGRAM.CALL_TO_ACTION_TEXT', {
        value: this.settingsService.getReferralProgramCredits(),
        currency: this.settingsService.getCurrencySymbol(),
      }) as string;
    }

    this.updateAccountStatus(this.accountService.isLoggedIn());

    this.appEventsService
      .onEvent('UserLoginStatusChanged')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoggedIn: boolean) => this.updateAccountStatus(isLoggedIn));

    this.activeOrdersCount$ = this.ordersService.activeOrders$.pipe(
      map((details) => details.count),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
    );

    this.cartItemsCount$ = this.cartService.cart$.pipe(
      map(() => this.cartService.getItemsCount()),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
    );
  }

  private updateAccountStatus(isLoggedIn: boolean): void {
    this.isLoggedIn = isLoggedIn;

    if (isLoggedIn) {
      this.accountService.getUserRewards().subscribe((userRewards) => (this.walletCredits = userRewards?.wallet?.balance));
      this.adminUrl = this.accountService.userDetails.adminUrl;
    }
  }
}
